import api from "@/api";
import deepClone from "@/utils/deep-clone";

import { alphabetically } from "@/utils/sort-object";

const defaultPath = {
    name: "",
    description: "",
    admins: [],
    steps: [],
    access: {
        selfenrol: {
            mode: "none",
            targeting: {
                skills: {
                    include: [],
                },
            },
        },
        targeting: {
            mode: "none",
            timing: "dynamic",
            oneOff: {
                enabled: false,
                date: new Date(Date.now()),
            },
            hireDateDelay: {
                enabled: true,
                duration: "P2W", // ISO Duration
            },
            all: {
                enabled: true,
            },
            users: {
                include: [],
                exclude: [],
            },
            teams: {
                include: [],
                exclude: [],
            },
        },
    },
    status: "draft",
};

const filters = [
    {
        key: "status",
        value: null,
        apply(list) {
            if (!this.value) {
                return list;
            }
            return list.filter(item => item[this.key] === this.value);
        },
    },
    {
        key: "createdBy",
        value: null,
        apply(list) {
            if (!this.value) {
                return list;
            }
            return list.filter(item => item[this.key]?._id === this.value);
        },
    },
    {
        key: "targeting",
        value: null,
        apply(list) {
            if (!this.value) {
                return list;
            }

            if (this.value === "all") {
                return list.filter(item => item?.access?.targeting?.all?.enabled);
            }

            return list.filter(item =>
                item?.access?.targeting?.teams?.include?.find(team => team._id === this.value),
            );
        },
    },
];

const state = {
    details: {},
    list: [],
    filters,
    stats: {},
};

const getters = {
    hasElements(state) {
        return !!state.list.length;
    },
    list(state) {
        return deepClone(state.list);
    },
    filteredList(state) {
        const filteredList = state.filters.reduce((list, filter) => filter.apply(list), state.list);

        return deepClone(filteredList);
    },
    filters(state) {
        // Filter by status
        const uniqueStatus = [...new Set(state.list.map(item => item.status))];
        const filterByStatusOptions = [
            { value: "", label: "all" },
            ...uniqueStatus.map(status => ({ value: status, label: status })),
        ];
        const filterByStatus = {
            key: "status",
            options: filterByStatusOptions,
            value: state.filters.find(filter => filter.key === "status")?.value,
        };

        // Filter by createdBy
        const uniqueCreators = state.list
            .map(item => item.createdBy)
            .filter(Boolean)
            .filter((user, index, users) => index === users.findIndex(u => u._id === user._id));

        const filterByCreatedByOptions = [
            { value: "", label: "any" },
            ...uniqueCreators.map(user => ({
                value: user._id,
                label: "",
                text: user.firstname + " " + user.lastname,
            })),
        ];
        const filterByCreatedBy = {
            key: "createdBy",
            options: filterByCreatedByOptions,
            value: state.filters.find(filter => filter.key === "createdBy")?.value,
        };

        // Filter by targeting
        const uniqueTeamsTargeting = state.list
            // Remove items that target all employees
            .filter(item => !item.access?.targeting?.all?.enabled)
            // Extract the teams in targeting
            .map(item => item.access?.targeting?.teams?.include)
            // Filter out items with no teams in targeting
            .filter(item => item.length)
            // Flattend the Array
            .flat()
            // Remove duplicates
            .filter((team, index, teams) => index === teams.findIndex(t => t._id === team._id));

        const filterByTeamsTargetingOptions = [
            { value: "", label: "any" },
            { value: "all", label: "all" },
            ...uniqueTeamsTargeting.map(team => ({
                value: team._id,
                label: "",
                text: team.name,
            })),
        ];

        const filterByTeamsTargeting = {
            key: "targeting",
            options: filterByTeamsTargetingOptions,
            value: state.filters.find(filter => filter.key === "targeting")?.value,
        };

        return deepClone([filterByStatus, filterByCreatedBy, filterByTeamsTargeting]);
    },
    search(state) {
        return query =>
            getters
                .filteredList(state)
                .filter(item => item.name?.toLowerCase().includes(query.toLowerCase()));
    },
    details(state) {
        return id => deepClone(state.details[id]);
    },
    stats(state) {
        return id => deepClone(state.stats[id]);
    },
};

const actions = {
    getList({ commit }) {
        return api.get("/api/learning/path").then(res => {
            const list = res.data && res.data.items ? res.data.items : [];
            commit("updateList", {
                // Apply default sorting before saving in `state`
                list: list.sort(alphabetically("name")),
            });
        });
    },
    get({ commit, state }, { payload: { id, cache } }) {
        if (cache) {
            const details = getters.details(state)(id);
            if (details && Object.keys(details).length) {
                return Promise.resolve();
            }
        }

        return (
            api
                .get(`/api/learning/path/${id}`)
                .then(res => {
                    commit("updateDetails", {
                        path: {
                            commonId: id,
                            ...defaultPath,
                            ...res.data,
                        },
                    });
                })
                /**
                 * As we are doing upserts for creating and editing new content,
                 * we will face a lot of `404`s when creating content.
                 *
                 * As such, we don't want to throw an error here so instead
                 * we commit with an empty object.
                 *
                 */
                .catch(err => {
                    if (err?.response?.status === 404) {
                        commit("updateDetails", {
                            path: {
                                commonId: id,
                                isNew: true,
                                ...defaultPath,
                            },
                        });
                        return;
                    }

                    throw err;
                })
        );
    },
    getStats({ commit }, { payload: { id } }) {
        return api.get(`/api/learning/path/${id}/stats`).then(res => {
            commit("updateStats", {
                id,
                stats: { ...res.data },
            });
        });
    },
    update({ commit }, { payload: { path } }) {
        return api.put(`/api/learning/path/${path.commonId}`, path).then(res => {
            commit("updateDetails", { path: res.data });
        });
    },
    delete({ commit }, { payload: { id } }) {
        return api.delete(`/api/learning/path/${id}`).then(() => {
            commit("deleteDetails", { id });
        });
    },
    filterBy({ commit, state }, { payload: { key, value } }) {
        const filters = state.filters.map(filter => {
            if (filter.key === key) {
                return { ...filter, value };
            }

            return filter;
        });

        commit("updateFilters", { filters });
    },
};

const mutations = {
    updateList(state, { list }) {
        state.list = [...list];
    },
    updateDetails(state, { path }) {
        // select targeting mode
        switch (true) {
            case path.access.targeting.all.enabled:
                path.access.targeting.mode = "all";
                break;
            case Boolean(path.access.targeting.users.include.length) ||
                Boolean(path.access.targeting.teams.include.length):
                path.access.targeting.mode = "selectedParticipants";
                break;
            default:
                path.access.targeting.mode = "none";
                break;
        }

        // select timing UI option
        switch (true) {
            case path.access.targeting.hireDateDelay.enabled:
                path.access.targeting.timing = "dynamic";
                break;
            case path.access.targeting.oneOff.enabled:
                path.access.targeting.timing = "oneOff";
                break;
            default:
                path.access.targeting.timing = "immediate";
                break;
        }

        if (!path.access.targeting.oneOff.date) {
            // set default date
            path.access.targeting.oneOff.date = new Date(Date.now());
        }

        state.details = {
            ...state.details,
            [path.commonId]: path,
        };
    },
    deleteDetails(state, { id }) {
        delete state.details[id];
    },
    updateStats(state, { id, stats }) {
        state.stats = {
            ...state.stats,
            [id]: { ...stats },
        };
    },
    updateFilters(state, { filters }) {
        state.filters = [...filters];
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};

export { filters as __test__filters };
